import { useMemo } from "react";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";
import ReportLayout from "@/components/reports/ReportLayout";
import useImpression from "@/hooks/useImpression";
import SummaryCard from "@/components/reports/SummaryCard";
import DataTableWrapper, { type Column } from "@/components/reports/DataTableWrapper";
import ExportButton from "@/components/reports/ExportButton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { UserCourseProgress, UserAssessmentHistory } from "@/types/reports";
import { useAppI18n } from "@/hooks/useAppI18n";
import { useUserCourseEnrolments } from "@/hooks/useUserCourseEnrolments";
import { useUserRead } from "@/hooks/useUserRead";
import { useUserAssessmentHistory } from "@/hooks/useUserAssessmentHistory";
import { mapApiItemToUserCourseProgress, mapApiItemToUserAssessmentHistory } from "@/utils/userCourseEnrolmentUtils";

const statusColor: Record<string, string> = {
  "Completed": "default",
  "In Progress": "secondary",
  "Not Started": "outline",
};

const UserReport = () => {
  const { userId } = useParams();
  // userId is 'me' on the self-service route (/reports/user/me), which mirrors
  // the reference portal's convention of using 'me' as a proxy for the logged-in user.
  useImpression({ type: 'view', pageid: 'user-report', env: 'reports', object: { id: userId || '', type: 'User' } });
  const { t } = useAppI18n();

  const { data: userReadData } = useUserRead();
  const userProfile = userReadData?.data?.response as { firstName?: string; lastName?: string } | undefined;
  const userName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ');

  const {
    data: enrolmentResult,
    isLoading: isCourseProgressLoading,
    isError: isCourseProgressError,
  } = useUserCourseEnrolments();

  const apiCourses = enrolmentResult?.data ?? [];

  /** Derived summary values */
  const summaryCoursesCompleted = isCourseProgressLoading
    ? '—'
    : String(apiCourses.filter((c) => c.status === 2).length);
  const summaryCoursesPending = isCourseProgressLoading
    ? '—'
    : String(apiCourses.filter((c) => c.status !== 2).length);
  const summaryTotalCourses = isCourseProgressLoading
    ? '—'
    : String(enrolmentResult?.count ?? 0);
  const summaryCertsIssued = isCourseProgressLoading
    ? '—'
    : String(apiCourses.filter((c) => c.issued_certificates != null).length);

  const courseProgressData = useMemo(
    () =>
      orderBy(
        apiCourses,
        [(c) => (c.datetime ? new Date(c.datetime).getTime() : 0)],
        ["desc"]
      ).map(mapApiItemToUserCourseProgress),
    [apiCourses]
  );

  const {
    data: assessmentResult,
    isLoading: isAssessmentsLoading,
    isError: isAssessmentsError,
  } = useUserAssessmentHistory();

  const assessmentHistoryData = useMemo(
    () =>
      orderBy(
        (assessmentResult?.data ?? []),
        [(a) => (a.last_attempted_on ? new Date(a.last_attempted_on).getTime() : 0)],
        ["desc"]
      ).map(mapApiItemToUserAssessmentHistory),
    [assessmentResult]
  );

  const summaryAssessmentsCompleted = isAssessmentsLoading
    ? '—'
    : String(assessmentResult?.count ?? 0);

  const courseColumns: Column<UserCourseProgress>[] = [
    { key: "courseName", header: t('userReport.course'), sortable: true },
    {
      key: "progressPercent",
      header: t('userReport.progress'),
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2 min-w-[120px]">
          <Progress value={row.progressPercent} className="h-2 flex-1" />
          <span className="text-xs font-medium w-8 text-right">{row.progressPercent}%</span>
        </div>
      ),
    },
    {
      key: "status",
      header: t('userReport.status'),
      render: (row) => <Badge variant={statusColor[row.status] as "default"} className="text-xs">{row.status}</Badge>,
    },
    { key: "enrollmentDate", header: t('userReport.enrolled'), sortable: true },
    { key: "lastAccessedTs", header: t('userReport.lastAccessed'), sortable: true, render: (r) => r.lastAccessed },
  ];

  const assessColumns: Column<UserAssessmentHistory>[] = [
    { key: "courseName", header: t('userReport.course'), sortable: true },
    { key: "assessmentName", header: t('userReport.assessment'), sortable: true },
    { key: "score", header: t('userReport.score'), sortable: true, className: "text-right" },
    { key: "maxScore", header: t('userReport.max'), className: "text-right" },
    { key: "percentage", header: "%", sortable: true, className: "text-right", render: (r) => `${r.percentage}%` },
    { key: "attemptDateTs", header: t('userReport.dateTime'), sortable: true, render: (r) => r.attemptDate },
  ];

  return (
    <ReportLayout
      title={`${t('userReport.title')}: ${userName}`}
      breadcrumbs={[{ label: t('home'), href: "/home" }, { label: t('userReport.title') }]}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        <SummaryCard label={t('userReport.totalCourses')} value={summaryTotalCourses} colorClass="bg-sunbird-wave" />
        <SummaryCard label={t('userReport.coursesCompleted')} value={summaryCoursesCompleted} colorClass="bg-sunbird-moss" />
        <SummaryCard label={t('userReport.coursesPending')} value={summaryCoursesPending} colorClass="bg-sunbird-theme-accent-muted" />
        <SummaryCard label={t('userReport.certificatesIssued')} value={summaryCertsIssued} colorClass="bg-sunbird-ink" />
        <SummaryCard label={t('userReport.assessmentsCompleted')} value={summaryAssessmentsCompleted} colorClass="bg-sunbird-lavender" />
      </div>

      {/* Course Progress */}
      <section className="mb-8" aria-label={t('userReport.courseProgress')}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">{t('userReport.courseProgress')}</h2>
          <ExportButton
            data={courseProgressData as unknown as Record<string, unknown>[]}
            filename="user-course-progress"
            columns={courseColumns.map((c) => ({ key: c.key, header: c.header }))}
          />
        </div>

        {isCourseProgressLoading && (
          <div
            className="flex items-center justify-center py-16 text-sm text-muted-foreground"
            data-testid="course-progress-loading"
          >
            Loading course progress…
          </div>
        )}

        {isCourseProgressError && !isCourseProgressLoading && (
          <div
            className="flex items-center justify-center py-16 text-sm text-destructive"
            data-testid="course-progress-error"
          >
            Failed to load course progress. Please try again.
          </div>
        )}

        {!isCourseProgressLoading && !isCourseProgressError && (
          <DataTableWrapper columns={courseColumns} data={courseProgressData} keyExtractor={(r) => r.id} pageSize={10} />
        )}
      </section>

      {/* Assessment History */}
      <section className="mb-8" aria-label={t('userReport.assessmentHistory')}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">{t('userReport.assessmentHistory')}</h2>
          <ExportButton
            data={assessmentHistoryData as unknown as Record<string, unknown>[]}
            filename="user-assessments"
            columns={assessColumns.map((c) => ({ key: c.key, header: c.header }))}
          />
        </div>

        {isAssessmentsLoading && (
          <div
            className="flex items-center justify-center py-16 text-sm text-muted-foreground"
            data-testid="assessments-loading"
          >
            Loading assessment history…
          </div>
        )}

        {isAssessmentsError && !isAssessmentsLoading && (
          <div
            className="flex items-center justify-center py-16 text-sm text-destructive"
            data-testid="assessments-error"
          >
            Failed to load assessment history. Please try again.
          </div>
        )}

        {!isAssessmentsLoading && !isAssessmentsError && (
          <DataTableWrapper columns={assessColumns} data={assessmentHistoryData} keyExtractor={(r) => r.id} pageSize={10} />
        )}
      </section>
    </ReportLayout>
  );
};

export default UserReport;
